import { For, Show, createComputed, createMemo, createSignal } from "solid-js";
import { api } from "../api/client";
import type { ReminderLeadMinutes } from "../api/types";
import { setAppLocale, t, locale } from "../i18n";
import { createCachedResource } from "../lib/cache";
import {
  formatMinutesProgress,
  minutesProgress,
  planGoalSave,
} from "../lib/goalMinutes";
import { showStreakCelebration } from "../lib/streakCelebration";
import { useAuth } from "../stores/auth";
import Avatar from "../components/Avatar";
import PageHero from "../components/PageHero";
import ProgressBar from "../components/ProgressBar";
import Cell from "../components/tg/Cell";
import Section from "../components/tg/Section";

const REMINDER_LEAD_OPTIONS: ReminderLeadMinutes[] = [15, 30, 60, 120];

export default function ProfilePage() {
  const auth = useAuth();
  const [countInput, setCountInput] = createSignal("");
  const [countDirty, setCountDirty] = createSignal(false);
  const [minutesInput, setMinutesInput] = createSignal("");
  const [minutesDirty, setMinutesDirty] = createSignal(false);
  const [attempted, setAttempted] = createSignal(false);
  const [saving, setSaving] = createSignal(false);
  const minutesSupported = () => auth.profile()?.weeklyGoalMinutes !== undefined;
  // The fields follow the stored goals until the user edits them.
  createComputed(() => {
    const pr = auth.profile();
    if (!countDirty()) {
      // Old backends keep the previous default of 3 workouts.
      setCountInput(String(pr?.weeklyGoal?.targetCount ?? (minutesSupported() ? "" : 3)));
    }
    if (!minutesDirty()) setMinutesInput(pr?.weeklyGoalMinutes ? String(pr.weeklyGoalMinutes) : "");
  });
  const plan = createMemo(() =>
    planGoalSave({
      supported: minutesSupported(),
      storedCount: auth.profile()?.weeklyGoal?.targetCount ?? null,
      storedMinutes: auth.profile()?.weeklyGoalMinutes ?? null,
      countText: countInput(),
      countDirty: countDirty(),
      minutesText: minutesInput(),
      minutesDirty: minutesDirty(),
    }),
  );
  const planError = (k: "count" | "minutes" | "general") => {
    const pl = plan();
    if (pl.ok) return null;
    if (k === "general" && !attempted()) return null;
    return pl[k] ?? null;
  };
  const [weightSummary] = createCachedResource(() => true, {
    key: () => "weight:summary:30d",
    fetch: () => api.bodyWeightSummary("30d"),
  });

  async function saveGoal() {
    setAttempted(true);
    const pl = plan();
    if (!pl.ok || !pl.body) return;
    setSaving(true);
    try {
      await api.setWeeklyGoal(pl.body);
      setCountDirty(false);
      setMinutesDirty(false);
      setAttempted(false);
      await auth.refreshProfile();
    } finally {
      setSaving(false);
    }
  }

  async function switchLang(lang: "ru" | "en") {
    setAppLocale(lang);
    await api.setLanguage(lang);
    await auth.refreshProfile();
  }

  async function setReminderLead(minutes: ReminderLeadMinutes) {
    setSaving(true);
    try {
      await api.setReminderLead(minutes);
      await auth.refreshProfile();
    } finally {
      setSaving(false);
    }
  }

  const p = () => auth.profile();
  const reminderLead = (): ReminderLeadMinutes =>
    p()?.reminderLeadMinutes ?? 60;

  return (
    <div>
      <PageHero title={t("PROFILE")} />

      <Show when={p()}>
        {(profile) => (
          <>
            <Section>
              <div class="tg-cell">
                <Avatar letter={profile().avatarLetter} size="md" />
                <span class="tg-cell-body">
                  <span class="tg-cell-title">
                    {profile().firstName}
                    {profile().lastName ? ` ${profile().lastName}` : ""}
                  </span>
                  <Show when={profile().username}>
                    <span class="tg-cell-subtitle">@{profile().username}</span>
                  </Show>
                </span>
              </div>
            </Section>

            <Section header={t("STREAK")}>
              <Cell
                title={t("STREAK")}
                value={`${profile().streak} ${t("WEEKS")}`}
                onClick={() => {
                  const n = profile().streak;
                  showStreakCelebration(Math.max(0, n - 1), Math.max(1, n));
                }}
              />
              {/* Only a minutes goal is set: the count row would read "0/—". */}
              <Show when={profile().weeklyGoal || !minutesProgress(profile())}>
                <Cell
                  title={t("WEEKLY_GOAL")}
                  value={`${profile().weeklyGoal?.completedCount ?? 0}/${
                    profile().weeklyGoal?.targetCount ?? "—"
                  }`}
                />
              </Show>
              <Show when={profile().weeklyGoal}>
                {(g) => (
                  <div class="px-4 pb-3">
                    <ProgressBar
                      value={g().completedCount}
                      max={g().targetCount}
                    />
                  </div>
                )}
              </Show>
              <Show when={minutesProgress(profile())}>
                {(m) => (
                  <>
                    <Cell
                      title={t("WEEKLY_MINUTES")}
                      value={formatMinutesProgress(m().done, m().goal)}
                    />
                    <div class="px-4 pb-3">
                      <ProgressBar value={m().done} max={m().goal} />
                      <div class="field-hint">{t("MINUTES_UNTIMED_NOT_COUNTED")}</div>
                    </div>
                  </>
                )}
              </Show>
            </Section>

            <Section>
              <Cell
                href="/workouts"
                title={t("ALL_WORKOUTS")}
                subtitle={t("ALL_WORKOUTS_HINT")}
                chevron
              />
              <Cell
                href="/templates"
                title={t("TEMPLATES")}
                subtitle={t("FROM_TEMPLATE_HINT")}
                chevron
              />
              <Cell
                href="/weight"
                title={t("CURRENT_WEIGHT")}
                value={
                  weightSummary()?.current
                    ? `${weightSummary()!.current!.weight.toFixed(1)} ${t("KG")}`
                    : "—"
                }
                chevron
              />
            </Section>

            <Section header={t("SET_GOAL")}>
              <div class="px-4 py-3">
                <label class="label">{t("TARGET_WORKOUTS")}</label>
                <input
                  class={`input tabular-nums${planError("count") ? " is-invalid" : ""}`}
                  type="text"
                  inputmode="numeric"
                  enterkeyhint="done"
                  autocomplete="off"
                  placeholder="—"
                  aria-label={t("TARGET_WORKOUTS")}
                  aria-invalid={planError("count") ? "true" : "false"}
                  value={countInput()}
                  onInput={(e) => {
                    setCountDirty(true);
                    setCountInput(e.currentTarget.value);
                  }}
                />
                <Show when={planError("count")}>
                  <div class="field-hint is-error" role="alert">
                    {planError("count")}
                  </div>
                </Show>
              </div>
              <Show when={minutesSupported()}>
                <div class="px-4 pb-3">
                  <label class="label">{t("TARGET_MINUTES")}</label>
                  <input
                    class={`input tabular-nums${planError("minutes") ? " is-invalid" : ""}`}
                    type="text"
                    inputmode="text"
                    enterkeyhint="done"
                    autocomplete="off"
                    autocorrect="off"
                    spellcheck={false}
                    placeholder={t("DURATION_HM_HINT")}
                    aria-label={t("TARGET_MINUTES")}
                    aria-invalid={planError("minutes") ? "true" : "false"}
                    value={minutesInput()}
                    onInput={(e) => {
                      setMinutesDirty(true);
                      setMinutesInput(e.currentTarget.value);
                    }}
                  />
                  <Show when={planError("minutes")}>
                    <div class="field-hint is-error" role="alert">
                      {planError("minutes")}
                    </div>
                  </Show>
                </div>
              </Show>
              <Show when={planError("general")}>
                <div class="field-hint is-error px-4 pb-3" role="alert">
                  {planError("general")}
                </div>
              </Show>
              <Cell
                title={t("SAVE")}
                disabled={saving()}
                onClick={saveGoal}
              />
            </Section>

            <Section header={t("LANGUAGE")}>
              <Cell
                title="Русский"
                value={locale() === "ru" ? "✓" : ""}
                onClick={() => switchLang("ru")}
              />
              <Cell
                title="English"
                value={locale() === "en" ? "✓" : ""}
                onClick={() => switchLang("en")}
              />
            </Section>

            <Section header={t("REMINDER_LEAD")} footer={t("REMINDER_LEAD_HINT")}>
              <For each={REMINDER_LEAD_OPTIONS}>
                {(m) => (
                  <Cell
                    title={
                      m === 60
                        ? t("REMINDER_LEAD_1H")
                        : m === 120
                          ? t("REMINDER_LEAD_2H")
                          : `${m} ${t("DURATION_MIN")}`
                    }
                    value={reminderLead() === m ? "✓" : ""}
                    disabled={saving()}
                    onClick={() => void setReminderLead(m)}
                  />
                )}
              </For>
            </Section>

            <Show when={profile().isAdmin}>
              <Section>
                <Cell href="/admin" title={t("ADMIN_PANEL")} chevron />
              </Section>
            </Show>

            <Section>
              <Cell
                title={t("LOGOUT")}
                destructive
                onClick={() => auth.logout()}
              />
            </Section>
          </>
        )}
      </Show>
    </div>
  );
}
